import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import SellFromHome from '../../../assets/images/sell-from-home.png';
import { ReactComponent as ArrowRight } from '../../../assets/images/svg/arrow-right-blue.svg';
import { ReactComponent as Lock } from '../../../assets/images/svg/lock.svg';
import { mediaBelow } from '../../../assets/styles/helper';
import { variables } from '../../../assets/styles/variables';
import { ReactComponent as InfoIcon } from '../../../assets/images/svg/info-filled-blue.svg';
import themeColors from '../../../assets/styles/themeColors';
import useSellFromHomeEligibility from '../../../util/hooks/useSellFromHomeEligibility';
import NonUsSellFromHomeDrawer from './NonUsSellFromHomeDrawer';
import BasicAccountRestristionsModal from '../BasicAccountRestrictionsModal/BasicAccountRestrictionsModal';
import SellFromHomeSsnLockedModal from './SellFromHomeSsnLockedModal';

const BannerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: ${themeColors.colorBannerBg};
  padding: 12px 16px;
  margin: 36px -16px 0;
  cursor: pointer;
`;

const BannerLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const BannerText = styled.div`
  display: flex;
  flex-direction: column;
`;

const BannerTitle = styled.span`
  font-weight: 600;
  color: ${themeColors.colorLinkPrimary};
  font-size: 16px;

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 14px;
  }
`;

const BannerSubtitle = styled.span`
  font-weight: 400;
  color: ${themeColors.colorTextSecondary};
  font-size: 14px;
`;

const BannerCta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
`;

const BannerLockIcon = styled.span`
  display: flex;
  align-items: center;

  svg {
    width: 16px;
    height: 16px;
    color: ${themeColors.colorPrimary};
  }
`;

const BannerCtaLabel = styled.span`
  font-weight: 400;
  color: ${themeColors.colorPrimary};
  font-size: 14px;
  text-decoration: underline;
`;

const SellFromHomeBanner = ({ onOpenSFHWizard }) => {
  const { t } = useTranslation();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isLockedModalOpen, setIsLockedModalOpen] = useState(false);
  const {
    isGS,
    isNonUS,
    isBasicAccount,
    isSellFromHomeRestricted,
  } = useSellFromHomeEligibility();

  if (!isGS) return null;

  const handleBannerClick = () => {
    if (isSellFromHomeRestricted) {
      setIsLockedModalOpen(true);
      return;
    }
    if (!isNonUS && !isBasicAccount && onOpenSFHWizard) {
      onOpenSFHWizard();
      return;
    }
    setIsDrawerOpen(true);
  };

  return (
    <>
      <BannerContainer onClick={handleBannerClick}>
        <BannerLeft>
          <img src={SellFromHome} alt="Sell From Home" width={55} height={35} />
          <BannerText>
            <BannerTitle>{t('sellWizard.sellFromHome.title')}</BannerTitle>
            <BannerSubtitle>
              {t('sellWizard.sellFromHome.subtitle')}
            </BannerSubtitle>
          </BannerText>
        </BannerLeft>
        <BannerCta>
          {isSellFromHomeRestricted && (
            <BannerLockIcon aria-hidden>
              <Lock />
            </BannerLockIcon>
          )}
          <BannerCtaLabel>{t('sellWizard.sellFromHome.cta')}</BannerCtaLabel>
          {!isSellFromHomeRestricted && (
            <ArrowRight fill={themeColors.colorPrimary} />
          )}
        </BannerCta>
      </BannerContainer>

      <SellFromHomeSsnLockedModal
        isOpen={isLockedModalOpen}
        close={() => setIsLockedModalOpen(false)}
      />
      {isNonUS ? (
        <NonUsSellFromHomeDrawer
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
        />
      ) : (
        <BasicAccountRestristionsModal
          isOpen={isDrawerOpen}
          close={() => setIsDrawerOpen(false)}
          title={t('basicAccountRestrictions.verifyAccountSellFromHome.title')}
          description={t(
            'basicAccountRestrictions.verifyAccountSellFromHome.description',
          )}
          icon={<InfoIcon width={48} height={48} />}
          showDoItLater
        />
      )}
    </>
  );
};

SellFromHomeBanner.propTypes = {
  onOpenSFHWizard: PropTypes.func,
};

export default SellFromHomeBanner;
